import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAuth } from "~/Hooks/useAuth";
import { post, put } from "~/Services";
import { eramanHost } from "~/Utils";
import { fields } from "../constants";
import { useCopyrights } from "../Footer.hook";

export const Copyrights = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const { user } = useAuth();

  const { data: editData, error, isLoading: drilldownLoading, isError, refetch: refetchEditData } = useCopyrights();

  const {
    data: dataById,
    isLoading: isLoadingData,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    } else {
      refetchEditData();
    }
  }, [query.get("id")]);

  const AwardsFormConfig: FormConfig = {
    initialValues: {
      name: decodeHTML(dataById ? dataById.payload.name : editData ? editData?.[0]?.name : ""),
      field_copyrights_en: decodeHTML(
        dataById ? dataById.payload.field_copyrights_en?.value : editData ? editData?.[0].field_copyrights_en : ""
      ),
      field_copyrights_ms: decodeHTML(
        dataById ? dataById.payload.field_copyrights_ms?.value : editData ? editData?.[0]?.field_copyrights_ms : ""
      ),
      field_copyrights_zh: decodeHTML(
        dataById ? dataById.payload.field_copyrights_zh?.value : editData ? editData?.[0]?.field_copyrights_zh : ""
      ),
      field_privacy_policy_text_en: decodeHTML(
        dataById
          ? dataById.payload.field_privacy_policy_text_en
          : editData
            ? editData?.[0]?.field_privacy_policy_text_en
            : ""
      ),
      field_privacy_policy_text_ms: decodeHTML(
        dataById
          ? dataById.payload.field_privacy_policy_text_ms
          : editData
            ? editData?.[0]?.field_privacy_policy_text_ms
            : ""
      ),
      field_privacy_policy_text_zh: decodeHTML(
        dataById
          ? dataById.payload.field_privacy_policy_text_zh
          : editData
            ? editData?.[0]?.field_privacy_policy_text_zh
            : ""
      ),
      field_copyrights_pdf_en: dataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "field_copyrights_pdf_en.pdf",
              status: "done",
              url: dataById?.payload?.field_copyrights_pdf_en,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "field_copyrights_pdf_en.pdf",
              status: "done",
              url: editData?.[0]?.field_copyrights_pdf_en,
            },
          ]),
      field_copyrights_pdf_ms: dataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "field_copyrights_pdf_ms.pdf",
              status: "done",
              url: dataById?.payload?.field_copyrights_pdf_ms,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "field_copyrights_pdf_ms.pdf",
              status: "done",
              url: editData?.[0]?.field_copyrights_pdf_ms,
            },
          ]),
      field_copyrights_pdf_zh: dataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "field_copyrights_pdf_zh.pdf",
              status: "done",
              url: dataById?.payload?.field_copyrights_pdf_zh,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "field_copyrights_pdf_zh.pdf",
              status: "done",
              url: editData?.[0]?.field_copyrights_pdf_zh,
            },
          ]),
    },
    fields: fields.copyrights,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["field_copyrights_pdf_en", "field_copyrights_pdf_ms", "field_copyrights_pdf_zh"];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field?.[0]?.originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "ERAMAN");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else if (field?.[0]?.url) {
          data[key] = field[0].url;
        } else {
          data[key] = ""; // fallback in case of invalid field
        }
      } catch (error) {
        console.error("Image processing failed for:", key, error);
      }
    }

    let descKeys: any = ["field_copyrights_ms", "field_copyrights_en", "field_copyrights_zh"];
    for (const key of descKeys) {
      try {
        data[key] = {
          value: data[key] || "",
          format: "full_html",
        };
      } catch (error) {}
    }

    const payload = {
      action: action,
      method: method,
      module: "Footer",
      subModule: `copyrights`,
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...(action === "UPDATE" && { id: editData?.[0]?.id || dataById?.payload?.id }),
        ...data,
        published: true,
      },
      status: status,
    };
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
          // refetchDocument();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <>
      <DynamicForm
        isLoading={isLoadingData || drilldownLoading}
        config={AwardsFormConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${eramanHost}/api/get-footer-copyrights-taxonomy-items-data-list/${editData?.[0]?.id || dataById?.payload?.id}`,
            "UPDATE",
            "PATCH",
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
      />
    </>
  );
};
